<script setup lang="ts">
  import { Field } from 'vee-validate'
  import { QUESTIONNAIRE_OPEN_ANSWER_MAX_LENGTH, type QuestionnaireItemModel } from '@profilpublic/api-contract/questionnaire'
  import { questionnaireFieldName } from '~/utils/schema/questionnaire-schema'

  const props = defineProps<{
    items: QuestionnaireItemModel[]
    errors: Record<string, string | undefined>
    submitCount?: number
  }>()

  const open = ref(false)

  const hasRequired = computed(() => props.items.some(item => item.required))

  watch(
    () => [props.submitCount, props.errors] as const,
    () => {
      if (!props.submitCount) return
      if (Object.keys(props.errors).some(key => key.startsWith('questionnaireAnswer'))) open.value = true
    }
  )
</script>

<template>
  <div>
    <button
      class="flex w-full items-center space-x-2 text-left font-special font-bold hover:opacity-80"
      type="button"
      @click="open = !open"
    >
      <Icon fixed-width name="chevron-right" :class="['duration-300', open ? 'rotate-90' : 'rotate-0']" />
      <span>Questions de candidature<span class="ml-0.5 text-zinc-400" v-if="hasRequired">*</span></span>
    </button>
    <Transition name="collapse">
      <div class="pt-3" v-show="open">
        <div class="pt-2 first:pt-0" v-for="item in items" :key="item.id">
          <template v-if="item.kind === 'open'">
            <p>{{ item.label }}<span class="ml-0.5 text-zinc-400" v-if="item.required">*</span></p>
            <FormField
              class="basis-full pt-2"
              :id="questionnaireFieldName(item)"
              :error="props.errors[questionnaireFieldName(item)]"
              :name="questionnaireFieldName(item)"
              :required="item.required"
              rows="4"
              type="textarea"
            />
            <p class="pt-1 text-sm text-zinc-500">{{ QUESTIONNAIRE_OPEN_ANSWER_MAX_LENGTH }} caractères maximum</p>
          </template>
          <template v-else-if="item.kind === 'closed'">
            <p>{{ item.label }}<span class="ml-0.5 text-zinc-400" v-if="item.required">*</span></p>
            <div class="flex items-center gap-4 pt-2">
              <div class="flex items-center space-x-2">
                <Field
                  class="form-radio"
                  :id="`${questionnaireFieldName(item)}_yes`"
                  :name="questionnaireFieldName(item)"
                  type="radio"
                  value="true"
                />
                <label :for="`${questionnaireFieldName(item)}_yes`">Oui</label>
              </div>
              <div class="flex items-center space-x-2">
                <Field
                  class="form-radio"
                  :id="`${questionnaireFieldName(item)}_no`"
                  :name="questionnaireFieldName(item)"
                  type="radio"
                  value="false"
                />
                <label :for="`${questionnaireFieldName(item)}_no`">Non</label>
              </div>
            </div>
            <p class="pt-1 text-sm text-red-500" v-if="props.errors[questionnaireFieldName(item)]">
              {{ props.errors[questionnaireFieldName(item)] }}
            </p>
          </template>
          <template v-else>
            <FormField
              class="basis-full"
              :id="questionnaireFieldName(item)"
              accept="application/pdf"
              :error="props.errors[questionnaireFieldName(item)]"
              :label="`${item.label} (format PDF)`"
              :name="questionnaireFieldName(item)"
              :required="item.required"
              type="file"
            />
          </template>
        </div>
      </div>
    </Transition>
  </div>
</template>
